import { Box, Stack, useTheme } from "@mui/material";
import { FoodsTextInput, Typography } from "@foodpilot/foods";
import { useId, type Dispatch, type SetStateAction } from "react";
import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { RadioBooleanField } from "src/components/Radio/RadioBooleanField";
import { SectionCard, SectionContent, SectionHeader } from "src/components/EntityForm";
import { formatFieldLabel } from "src/components/EntityForm/FoodsFieldPrimitives";

import type { IngredientFormType } from "../../IngredientForm.types";
import type { IngredientFormData } from "../../IngredientForm.schema";
import { IngredientReferenceField } from "../../Pages/Page1/IngredientReferenceField";

type IngredientIdentitySectionProps = {
    control: Control<IngredientFormData>;
    errors: FieldErrors<IngredientFormData>;
    ingredient: IngredientFormType;
    progress: { current: number; total: number };
    setIngredient: Dispatch<SetStateAction<IngredientFormType>>;
};

export const IngredientIdentitySection = (props: IngredientIdentitySectionProps) => {
    const { control, errors, ingredient, progress, setIngredient } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const nameErrorId = useId();
    const nameErrorMessage =
        errors.name?.message === "form.required_field" ?
            t("form.required_field", "This field is required.")
        :   errors.name?.message;

    return (
        <SectionCard>
            <SectionHeader
                progress={progress}
                title={t("entity.ingredient_identity.section_title", "Ingredient Identity")}
                subtitle={t(
                    "entity.ingredient_identity.section_subtitle",
                    "Define the information used to identify your ingredient and calculate its indicators.",
                )}
            />
            <SectionContent>
                <Stack sx={{ gap: theme.spacing(2) }}>
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Stack sx={{ gap: theme.spacing(1), minWidth: 0 }}>
                                <Typography variant="body-semibold">
                                    {formatFieldLabel(t("ingredient.identity.name", "Ingredient name"), true)}
                                </Typography>
                                <FoodsTextInput
                                    {...field}
                                    error={errors.name !== undefined}
                                    ExtraInputProps={{
                                        inputProps: {
                                            "aria-label": t("ingredient.identity.name", "Ingredient name"),
                                            "aria-describedby": nameErrorMessage ? nameErrorId : undefined,
                                            "aria-invalid": Boolean(nameErrorMessage),
                                            "aria-required": true,
                                        },
                                    }}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setIngredient((current) => ({ ...current, name: value }));
                                    }}
                                />
                                {nameErrorMessage && (
                                    <Typography
                                        id={nameErrorId}
                                        role="alert"
                                        variant="small"
                                        sx={{ color: theme.custom.red[600] }}
                                    >
                                        {nameErrorMessage}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    />
                    <Controller
                        name="organic"
                        control={control}
                        render={({ field }) => (
                            <RadioBooleanField
                                appearance="product-v2"
                                title={t("ingredient.identity.organic", "Organic ingredient")}
                                value={field.value}
                                onChange={(value) => {
                                    field.onChange(value);
                                    setIngredient((current) => ({ ...current, organic: value }));
                                }}
                            />
                        )}
                    />
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: theme.spacing(2),
                    }}
                >
                    <Controller
                        name="ingredientReferenceId"
                        control={control}
                        render={({ field }) => (
                            <IngredientReferenceField
                                appearance="product-v2"
                                ingredient={ingredient}
                                onChange={setIngredient}
                                field={field}
                            />
                        )}
                    />
                    <Controller
                        name="clientId"
                        control={control}
                        render={({ field }) => (
                            <Stack sx={{ gap: theme.spacing(1), minWidth: 0 }}>
                                <Typography variant="body-semibold">
                                    {t("product.identity.unique_identifier", "Unique identifier")}
                                </Typography>
                                <FoodsTextInput
                                    {...field}
                                    value={field.value ?? ""}
                                    error={errors.clientId !== undefined}
                                    ExtraInputProps={{
                                        inputProps: {
                                            "aria-label": t("product.identity.unique_identifier", "Unique identifier"),
                                        },
                                    }}
                                    onChange={(value: string) => {
                                        field.onChange(value);
                                        setIngredient((current) => ({ ...current, clientId: value }));
                                    }}
                                />
                            </Stack>
                        )}
                    />
                </Box>
            </SectionContent>
        </SectionCard>
    );
};
